/**
 * ExpressiveHome — §3 "media-forward consumer drama" dashboard variant.
 *
 * Layout:
 *   Row 1: [3/5] hero latest-event media card  +  [2/5] glass map card
 *   Row 2: Stat chips row (4 gradient icon tiles, font-display numerals)
 *   Row 3: 2-col mini-masonry of recent event media cards
 *
 * Data contract: receives ONLY what DashboardPage already computed — NO new fetches.
 */

import { useState } from 'react'
import { Camera, Activity, MapPin, Wifi, WifiOff, ImageIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Event, CameraMapPin } from '@/types'
import { DetectionChip } from '@/components/ui/detection-chip'
import { cn, formatRelativeTime } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export interface ExpressiveHomeProps {
  cameras: { id: number; is_active: boolean }[]
  totalCameras: number
  camerasLoading: boolean
  events: Event[]
  totalEvents: number
  eventsLoading: boolean
  mapPins: CameraMapPin[]
  setAnchor: (el: HTMLDivElement | null) => void
  /** Opens the event detail modal from the parent page */
  onEventClick?: (eventId: number) => void
}

// ── Mini media card (masonry items) ──────────────────────────────────────────

function MiniMediaCard({ event, onClick }: { event: Event; onClick?: () => void }) {
  const { t: tc } = useTranslation('common')
  const [imgError, setImgError] = useState(false)
  return (
    <div
      className={cn(
        'break-inside-avoid rounded-2xl overflow-hidden relative group cursor-pointer mb-4',
        'transition-all duration-[260ms]',
        onClick && 'hover:scale-[1.02] hover:shadow-elevation-3',
      )}
      style={{ minHeight: 120 }}
      onClick={onClick}
    >
      {event.thumbnail_url && !imgError ? (
        <img
          src={event.thumbnail_url}
          alt={event.camera_name}
          className="w-full object-cover block"
          style={{ minHeight: 120, maxHeight: 200 }}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="w-full bg-card flex items-center justify-center" style={{ minHeight: 120 }}>
          <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
        </div>
      )}

      {/* Bottom glass bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/55 backdrop-blur-sm px-3 py-2">
        <p className="text-white text-[11px] font-medium truncate leading-tight">{event.camera_name}</p>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="text-white/60 text-[10px]">{formatRelativeTime(event.occurred_at)}</span>
          {event.detected_objects?.length > 0 && (
            <span className="text-white/70 text-[10px] truncate">
              {tc(`detectionClasses.${event.detected_objects[0]}`, { defaultValue: event.detected_objects[0].replace(/_/g, ' ') })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Hero event card ───────────────────────────────────────────────────────────

function HeroEventCard({
  event,
  onClick,
}: {
  event: Event | null
  onClick?: () => void
}) {
  const { t: tc } = useTranslation('common')
  const [imgError, setImgError] = useState(false)

  if (!event) {
    // Graceful no-events placeholder — glass card
    return (
      <div
        data-testid="hero-event"
        className="rounded-2xl overflow-hidden relative min-h-[280px] bg-glass border border-border/50 flex flex-col items-center justify-center gap-3 text-center p-6"
      >
        <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
        <p className="font-display text-lg text-muted-foreground">{tc('hero.noEvents')}</p>
        <p className="text-sm text-muted-foreground/60">{tc('hero.noEventsHint')}</p>
      </div>
    )
  }

  return (
    <div
      data-testid="hero-event"
      className={cn(
        'rounded-2xl overflow-hidden relative min-h-[280px] group',
        'cursor-pointer',
      )}
      onClick={onClick}
    >
      {/* Background media */}
      {event.thumbnail_url && !imgError ? (
        <img
          src={event.thumbnail_url}
          alt={event.camera_name}
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-muted" />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      {/* Content overlay — bottom glass bar */}
      <div className="absolute bottom-0 left-0 right-0 backdrop-blur-sm bg-black/30 px-5 py-4">
        <p className="font-display text-xl text-white leading-tight truncate">{event.camera_name}</p>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {event.detected_objects?.slice(0, 3).map((cls) => (
            <DetectionChip key={cls} cls={cls} type="object" />
          ))}
          {event.audio_detections?.slice(0, 1).map((ad) => (
            <DetectionChip key={ad.class_name} cls={ad.class_name} type="audio" />
          ))}
        </div>
        <p className="text-white/60 text-[11px] mt-1.5">{formatRelativeTime(event.occurred_at)}</p>
      </div>
    </div>
  )
}

// ── ExpressiveHome ────────────────────────────────────────────────────────────

export function ExpressiveHome({
  cameras,
  totalCameras,
  camerasLoading,
  events,
  totalEvents,
  eventsLoading,
  mapPins,
  setAnchor,
  onEventClick,
}: ExpressiveHomeProps) {
  const { t } = useTranslation('cameras')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')

  const activeCameras = camerasLoading ? 0 : cameras.filter(c => c.is_active).length
  const onlineCameras = mapPins.filter(p => p.is_online).length

  const heroEvent = events[0] ?? null
  // Mini masonry: events after the hero, up to 8 items
  const masonryEvents = events.slice(1, 9)

  return (
    <div className="space-y-5">

      {/* ── Row 1: Hero (3/5) + Map (2/5) ── */}
      <div className="grid gap-4 lg:grid-cols-5">
        {/* Hero event card */}
        <div className="lg:col-span-3">
          <HeroEventCard
            event={heroEvent}
            onClick={heroEvent && onEventClick ? () => onEventClick(heroEvent.id) : undefined}
          />
        </div>

        {/* Glass map card */}
        <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-border/50 bg-glass flex flex-col min-h-[280px]">
          <div className="px-4 py-3 border-b border-border/30 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <span className="font-display text-sm font-medium">{t('map.title', { defaultValue: 'Camera Map' })}</span>
            {mapPins.length > 0 && (
              <span className="ml-auto text-[12px] text-muted-foreground">
                {t('map.mapped', { count: mapPins.length })}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-hidden min-h-0">
            {mapPins.length > 0 ? (
              <div
                ref={setAnchor}
                className="h-full min-h-0 w-full overflow-hidden"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-center p-4">
                <WifiOff className="h-7 w-7 text-muted-foreground/20" />
                <span className="text-sm text-muted-foreground">{t('map.noCoordinates', { defaultValue: 'No camera coordinates' })}</span>
                <Link to="/cameras" className="text-sm text-primary hover:text-primary/80 transition-colors">
                  {t('map.goToCameras', { defaultValue: 'Go to Cameras →' })}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 2: Stat chips ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t('dashboard.totalCameras'), value: totalCameras, loading: camerasLoading, icon: <Camera className="h-4 w-4" /> },
          { label: te('title', { defaultValue: 'Events' }), value: totalEvents, loading: eventsLoading, icon: <Activity className="h-4 w-4" /> },
          { label: t('dashboard.activeCameras'), value: activeCameras, loading: camerasLoading, icon: <Wifi className="h-4 w-4" /> },
          { label: tc('status.online', { defaultValue: 'Online' }), value: onlineCameras, loading: false, icon: <MapPin className="h-4 w-4" /> },
        ].map(({ label, value, loading, icon }) => (
          <div
            key={label}
            className="bg-glass border border-border/50 rounded-2xl px-4 py-3 flex items-center gap-3"
          >
            {/* Gradient icon tile */}
            <div className="h-9 w-9 rounded-xl grad-accent flex items-center justify-center flex-shrink-0 text-black">
              {icon}
            </div>
            <div className="min-w-0">
              {loading ? (
                <>
                  <div className="shimmer h-5 w-10 rounded mb-1" />
                  <div className="shimmer h-2.5 w-16 rounded" />
                </>
              ) : (
                <>
                  <span className="font-display text-xl tabular-nums text-foreground leading-none block">
                    {typeof value === 'number' ? value.toLocaleString() : value}
                  </span>
                  <span className="text-[11px] text-muted-foreground leading-tight block">{label}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Row 3: 2-col mini masonry of recent event media cards ── */}
      {!eventsLoading && masonryEvents.length > 0 && (
        <div className="columns-2 gap-4">
          {masonryEvents.map((event) => (
            <MiniMediaCard
              key={event.id}
              event={event}
              onClick={onEventClick ? () => onEventClick(event.id) : undefined}
            />
          ))}
        </div>
      )}

      {/* Loading shimmer for masonry */}
      {eventsLoading && (
        <div className="columns-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="break-inside-avoid rounded-2xl overflow-hidden mb-4">
              <div className="shimmer" style={{ height: i % 2 === 0 ? 140 : 180 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
